import type { DayViewProps } from '../components/DayView';
import { useCalendarEventInteractionCallbacks } from '../hooks/useCalendarEventInteractionCallbacks';
import { CalendarViewType } from '../schema';
import { getCalendarFolderIdForComposeFromFolderIds } from 'owa-calendar-forms-utils/lib/utils/getCalendarFolderIdForCompose';
import { convertToOwaDate, getStartOfWeek } from '../utils/dateUtils';
import { getTargetDivCSSSelector } from 'owa-calendar-surface-dategrid';
import type { CalendarEvent } from 'owa-calendar-types';
import { GridClickParams, selectDayGridDate } from 'owa-day-grid';
import type { DayGridViewState } from 'owa-day-grid-core-schema';
import { createQuickComposePreviewItem } from 'owa-quick-compose-controller';
import { useWheelStopPropagation } from 'owa-react-hooks/lib/useWheelStopPropagation';
import EventScope from 'owa-service/lib/contract/EventScope';
import { useAssetId } from 'owa-timestream-asset-sdk';
import { useCalendarAssetCallbacks } from 'owa-timestream-asset-sdk-calendars';
import * as React from 'react';

export default function useDayViewViewState({
    onOpenEventDetails,
    scenarioId,
    onDateUpdated,
    onContextMenuInvoked,
    onClickCalendarEvent,
    peekTarget,
    selectedViewType,
}: DayViewProps) {
    const ref = React.useRef<HTMLDivElement | null>(null);
    useWheelStopPropagation(ref);

    const assetId = useAssetId();

    // This is necessary because items in DayGridView rerender and the original HTMLElement gets invalidated. selector is consistent
    const targetSelector = peekTarget
        ? `div[data-assetId="${assetId}"] ${getTargetDivCSSSelector(peekTarget)}`
        : null;

    const { onOpenFullCompose, onOpenEdit } = useCalendarAssetCallbacks();

    const calendarEventInteractionCallback = useCalendarEventInteractionCallbacks(
        onOpenEventDetails,
        undefined,
        onClickCalendarEvent,
        onContextMenuInvoked
    );
    const isWeekOrWorkWeekView =
        selectedViewType == CalendarViewType.Week || selectedViewType == CalendarViewType.WorkWeek;

    const onSelectDate = React.useCallback(
        (selectedDate: Date) => {
            const owaSelectedDate = convertToOwaDate(selectedDate);
            onDateUpdated(selectedDate);
            selectDayGridDate(
                scenarioId,
                isWeekOrWorkWeekView ? getStartOfWeek(owaSelectedDate) : owaSelectedDate
            );
        },
        [isWeekOrWorkWeekView]
    );

    const onGridClick = React.useCallback(
        (params: GridClickParams & { viewState: DayGridViewState }) => {
            const { start, end, isAllDay, viewState, isClickAndDragEvent } = params;
            const composeCalendarFolderId = getCalendarFolderIdForComposeFromFolderIds(
                viewState?.folderIds
            );
            if (!composeCalendarFolderId) {
                return;
            }
            createQuickComposePreviewItem(
                viewState,
                start,
                end,
                isAllDay,
                isClickAndDragEvent,
                composeCalendarFolderId.Id
            );
        },
        [scenarioId]
    );

    const onGridDoubleClick = React.useCallback(
        (params: GridClickParams & { viewState: DayGridViewState }) => {
            const { start, end, isAllDay, viewState } = params;
            const composeCalendarFolderId = getCalendarFolderIdForComposeFromFolderIds(
                viewState?.folderIds
            );
            if (!composeCalendarFolderId) {
                return;
            }

            const initialCalendarEvent: Partial<CalendarEvent> = {
                Start: start,
                End: end,
                IsAllDayEvent: isAllDay,
                ParentFolderId: composeCalendarFolderId,
            };
            onOpenFullCompose(initialCalendarEvent);
        },
        [scenarioId, onOpenFullCompose]
    );

    const handleEditOnItemDrop = React.useCallback(
        (event: Partial<CalendarEvent>, eventHasUpdates: boolean) => {
            onOpenEdit(event, eventHasUpdates, EventScope.Default);
        },
        [onOpenEdit]
    );

    const preventAssetDrag = React.useCallback((mouseDownEvent: React.MouseEvent<HTMLElement>) => {
        mouseDownEvent.stopPropagation();
    }, []);

    return {
        ref: ref,
        targetSelector: targetSelector,
        calendarEventInteractionCallback: calendarEventInteractionCallback,
        onSelectDate: onSelectDate,
        onGridClick: onGridClick,
        onGridDoubleClick: onGridDoubleClick,
        handleEditOnItemDrop: handleEditOnItemDrop,
        preventAssetDrag: preventAssetDrag,
    };
}
